import React, {
  AnchorHTMLAttributes,
  FormEvent,
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
  useEffect,
  lazy,
} from 'react';
import { useRef } from 'react';
import { getUserInfo } from '../../utils/tokenUtils';
import { getPolicyGptFrameworkBasePath } from '../../constants/ssoConstants';
import safeJSONParse from '../../utils/safeJsonParse';
import {
  EArticleType,
  EBotType,
  EMessageType,
  EOptionAction,
  EV3StreamEventType,
  IbotError,
  IBotVariantConfig,
  IV3StreamEvent,
  OptionProps,
  TPayloadData,
} from '../../types/index';
import Layout from '../../components/Layout/Layout';
import AskMeAnything from '../../components/ChatFramework/AskMeAnything';
import Options from '../../components/ChatFramework/Options';
import { useAppContext } from '../../context/AppContext';
import {
  useResponsesContext,
  VariantConfigs,
  hasValueFunction,
} from '../../context/ResponseContext';
import { BackChevronMobile, DownwardArrowIcon } from 'ui/icons';
import {
  ALLOWED_PARENT_ORIGINS,
  APP_DEEPLINK_WEBOUTSIDE_URL,
  botWithContext,
  CALENDLY_URL,
  COOKIE_CONFIG,
  CUSTOMER_CARE_NUMBER,
  MIXPANEL_EVENTS,
  v3Bots,
} from '../../constants';
import useDuration from '../../hooks/useDuration';
import ReactMarkdown from 'react-markdown';
import { track } from '../../utils/track';
import gfm from 'remark-gfm';
import { invokeNativeBridge, postMessageToParent } from '../../utils/messageUtils';
import {
  buildCashlessClaimContext,
  shouldPostArticleToParent,
  usesIframeCloseBehaviour,
} from '../../utils/getCashlessPayload';
import { useLocation } from 'react-router-dom';
import AppConfig from '../../AppConfig';
import rehypeRaw from 'rehype-raw';
import { Button } from 'ui/mobile-ds';
import Cookies from 'js-cookie';
import ChatErrorScreen from '../../components/ChatFramework/ChatErrorScreen';
import useUserAgent from '../../hooks/useUserAgent';
import ThoughtBubble from '../../components/ChatFramework/ThoughtBubble';

const BottomSheet = lazy(() => import('ui/retail-ds/BottomDrawer'));

// Active claim id from the bot-context cookie (cashless support bot shape).
const getCookieClaimId = (): string => {
  const raw = Cookies.get(AppConfig.BOT_CONTEXT);
  const parsed = raw ? safeJSONParse(raw) : null;
  return parsed?.cashless_claim?.current_claim?.plumClaimId ?? '';
};

interface ILinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  children?: React.ReactNode;
  isWebView: boolean;
}

export const ScrollButton = ({
  handleScrollToBottomClick,
  isVisible,
}: {
  handleScrollToBottomClick: () => void;
  isVisible: boolean;
}) => {
  return isVisible ? (
    <div className="w-full flex justify-center mb-2">
      <div className="p-2 pl-3 bg-white flex border border-white rounded-[12px]">
        <button
          data-testid="down-arrow"
          className="sticky left-[30%] text-[14px] text-gray-700"
          onClick={handleScrollToBottomClick}
        >
          Scroll down
        </button>
        <DownwardArrowIcon className="ml-1" />
      </div>
    </div>
  ) : null;
};

export function InterceptedLink({ href, children, isWebView }: ILinkProps) {
  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (!href) {
      return;
    }

    if (!isWebView) {
      return;
    }

    if (href?.includes('tel:')) return;
    e.preventDefault();
    if (href?.includes('deeplink')) {
      invokeNativeBridge('callDeeplink', href);
    } else if (isWebView) {
      // Encode the wrapped URL: APP_DEEPLINK_WEBOUTSIDE_URL ends in `&id=`, so an
      // unencoded href carrying query params would let the native side truncate
      // at the first `&` (matches the sibling path in Options.tsx).
      invokeNativeBridge(
        'callDeeplink',
        `${APP_DEEPLINK_WEBOUTSIDE_URL}${encodeURIComponent(href)}`,
      );
    }
  };

  return (
    <a
      href={href}
      onClick={handleLinkClick}
      target={isWebView ? '_self' : '_blank'}
    >
      {children}
    </a>
  );
}

export const DisclaimerContent = ({
  handleClick,
}: {
  handleClick: () => void;
}) => {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-retail-text-primary text-2xl font-black">
        What is PolicyGPT?
      </p>
      <ul className="list-disc text-retail-text-secondary text-sm font-normal ml-4">
        <li className="mb-1">
          PolicyGPT is an experimental beta and may sometimes provide inaccurate
          information.
        </li>
        <li className="mb-1">
          Answers on specific treatments are not binding, and final claim
          approval depends on the Insurer’s document review and policy terms.
        </li>
        <li>
          Screenshots of responses from PolicyGPT cannot be used as proof for
          claims, coverage, or indemnification.
        </li>
      </ul>
      <Button role="disclaimer-button" intent="primary" onClick={handleClick}>
        I understand
      </Button>
    </div>
  );
};

// Function to handle different bot name and add response
export const handleBotError = ({
  botName,
  payloadData,
  queryIdRef,
  setResponses,
  setOptionClicked,
  statusCode,
  code,
  message = '',
}: IbotError) => {
  setOptionClicked(true);
  // Base error response
  const baseResponse = {
    id: queryIdRef.current++,
    query: '',
    response:
      statusCode === 419 && code
        ? message
        : botName
          ? getErrorMessage(botName)
          : 'There is some issue in getting response. Please refresh the page and try again',
    feedbackGiven: false,
    cid: '',
    date: new Date(),
    actionType: 'error',
  };

  // Determine additional responses based on botName
  const additionalResponses = (() => {
    if (statusCode === 419 && code) {
      return [
        {
          id: queryIdRef.current++,
          query: '',
          response: 'Reload Now',
          feedbackGiven: false,
          cid: '',
          date: new Date(),
          actionType: 'restart',
        },
      ];
    }

    switch (botName) {
      case EBotType.RTL_BOT:
        return [
          {
            id: queryIdRef.current++,
            query: '',
            response: 'Schedule Now',
            feedbackGiven: false,
            cid: '',
            date: new Date(),
            actionType: 'calendly',
          },
        ];

      case EBotType.CASHLESS_GPT_V2:
        return [
          {
            id: queryIdRef.current++,
            query: '',
            response:
              payloadData && payloadData.statusContent
                ? payloadData.statusContent.cashlessStatusPageContent
                    .customerSupportNumber
                : CUSTOMER_CARE_NUMBER,
            feedbackGiven: false,
            cid: '',
            date: new Date(),
            actionType: 'phone',
            content:
              payloadData && payloadData.statusContent
                ? payloadData.statusContent.cashlessStatusPageContent
                    .customerSupportNumber
                : CUSTOMER_CARE_NUMBER,
          },
        ];

      default:
        return [
          {
            id: queryIdRef.current++,
            query: '',
            response:
              'There is some issue in getting response. Please try to refresh the page',
            feedbackGiven: false,
            cid: '',
            date: new Date(),
            actionType: 'error',
          },
        ];
    }
  })();

  // Update responses with base and additional responses
  setResponses((prev) => [...prev, baseResponse, ...additionalResponses]);
};

export const getErrorMessage = (botName: string): string => {
  const botErrorMessage: { [key: string]: string } = {
    cashlessgptv2: `Looks like we're experiencing a temporary issue finding an answer to your question. Please reach out to our cashless support helpline for any assistance`,
    rtl_bot: `Uh-oh! Our Chatbot is offline. Need help from a human expert? Book a 1-on-1 here.`,
  };

  if (botErrorMessage[botName]) {
    return botErrorMessage[botName];
  } else {
    return `Unable to fetch answer.`;
  }
};

interface SessionValidationError {
  message: string;
  code?: string;
}

const ChatUI = () => {
  const user = getUserInfo();
  const { token, isWebView } = useAppContext();
  const {
    responses,
    setResponses,
    options,
    setOptions,
    setIsAnswerLoading,
    queryIdRef,
    setPayloadData,
    escalation,
    setEscalation,
    payloadData,
    setShowOptions,
    botName,
    variantConfig,
    setVariantConfig,
  } = useResponsesContext();
  const isV3Bot = useMemo(() => v3Bots.has(botName as EBotType), [botName]);
  const { operatingSystem } = useUserAgent();
  const [query, setQuery] = useState<string>('');
  const [chunkFinalMessage, setChunkFinalMessage] = useState<string>('');
  const screenBottom = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const previousConfigRef = useRef<IBotVariantConfig | null>(null);
  // Claim the current v3 session was created for; used to re-create it at send
  // time when the active claim changes (see getAnswer / validateSession).
  const sessionClaimIdRef = useRef<string>('');
  const isSubmitEnabled = query.trim();
  const [conversationid, setConversationId] = useState<string>('');
  const [optionClicked, setOptionClicked] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isInitialText, setIsInitialText] = useState<boolean>(true);
  const [loadingStates, setLoadingStates] = useState<Map<number, boolean>>(
    new Map(),
  );
  const [source, setSource] = useState<string>('');
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const { startTimer, pauseTimer } = useDuration();
  const location = useLocation();
  const [isValidatingSession, setIsValidatingSession] = useState(true);
  const [sessionId, setSessionId] = useState('');
  const [sessionError, setSessionError] =
    useState<SessionValidationError | null>(null);
  const [isSessionValid, setIsSessionValid] = useState(false);
  const [isOptionStreaming, setIsOptionStreaming] = useState(false);
  const [isDisclaimerBottomsheetOpen, setIsDisclaimerBottomsheetOpen] =
    useState(true);

  useEffect(() => {
    startTimer();
  }, []);

  useEffect(() => {
    const config = VariantConfigs.get(botName as any);
    track({
      event: MIXPANEL_EVENTS.BOT_LOADED,
      data: {
        payloadData,
        isWebView: String(isWebView),
      },
    });
    if (config) {
      setVariantConfig(config);
      window?.NativeBridge?.onLoadComplete?.();
    }
  }, [botName, setVariantConfig]);

  useEffect(() => {
    const handleScroll = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          chatContainerRef.current;
        const isScrolledToBottom =
          scrollHeight - scrollTop <= clientHeight + 150;
        setShowScrollButton(!isScrolledToBottom);
      }
    };

    // Check if the content is scrollable on initial render
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      const isScrolledToBottom = scrollHeight - scrollTop <= clientHeight + 10;
      setShowScrollButton(!isScrolledToBottom);
      chatContainerRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [responses]);

  const handleScrollToBottomClick = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  //function for learn more button click to open strapi article
  const handleStrapiArticleButtonClick = (
    content: string,
    articleType: string,
  ) => {
    if (
      shouldPostArticleToParent(
        botName,
        new URLSearchParams(window.location.search).get('parent'),
      )
    ) {
      postMessageToParent(EMessageType.OPEN_ARTICLE, content);
    } else if (articleType === EArticleType.WHATS_COVERED_WHATS_NOT_COVERED) {
      if (isWebView) {
        const url = `https://help.plumhq.com/en/wcwnc/${content}`;
        // Encode like the other deeplink call sites — APP_DEEPLINK_WEBOUTSIDE_URL
        // ends in `&id=`, so a raw url with query params would truncate at `&`.
        invokeNativeBridge(
          'callDeeplink',
          `${APP_DEEPLINK_WEBOUTSIDE_URL}${encodeURIComponent(url)}`,
        );
      } else window.open(`https://help.plumhq.com/en/wcwnc/${content}`);
    } else {
      window.open(source);
    }
  };

  const handleCalendlyLink = (url: string) => {
    window.open(url);
  };

  const validateSession = async () => {
    try {
      const frameworkBasePath = getPolicyGptFrameworkBasePath(user?.organisationId || '', user?.memberId || '', isV3Bot);
      const sessionUrl = isV3Bot
        ? `${frameworkBasePath}/v3/session`
        : `${frameworkBasePath}/${botName}/session`;

      let sessionBody = {};
      let claimId = '';
      if (isV3Bot) {
        const botContextCookie = Cookies.get(AppConfig.BOT_CONTEXT);
        const contextValues = botContextCookie ? JSON.parse(botContextCookie) : {};
        sessionBody = { contextValues };
        claimId = contextValues?.cashless_claim?.current_claim?.plumClaimId ?? '';
      }

      const response = await fetch(
        sessionUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-plum-token': token,
          },
          body: JSON.stringify(sessionBody),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Session validation failed');
      }

      const sessionData = await response.json();

      setIsValidatingSession(false);
      setIsSessionValid(true); // Set session as valid after successful validation
      setSessionId(sessionData.sessionId);
      sessionClaimIdRef.current = claimId;
      return sessionData.sessionId as string;
    } catch (error) {
      setSessionError({
        message:
          error instanceof Error ? error.message : 'Something went wrong',
        code: 'SESSION_ERROR',
      });
      setIsValidatingSession(false);
    }
  };

  const getAnswer = useCallback(
    async (messages: string, queryId: number) => {
      try {
        setIsStreaming(true);
        setIsOptionStreaming(true);
        setOptionClicked(false);
        setLoadingStates((prev) => new Map(prev.set(queryId, true)));
        const frameworkBasePath = getPolicyGptFrameworkBasePath(user?.organisationId || '', user?.memberId || '', isV3Bot);
        const chatUrl = isV3Bot
          ? `${frameworkBasePath}/v3/chat`
          : `${frameworkBasePath}/${botName}/chat/ask/stream`;
        const chatBody = isV3Bot
          ? {
              question: messages,
              contextValues: variantConfig?.contextValues ?? previousConfigRef.current?.contextValues ?? {},
            }
          : {
              question: messages,
              contextValues: hasValueFunction(variantConfig)
                ? {
                    ...variantConfig?.value(
                      variantConfig.contextValues?.payloadData ?? payloadData,
                    ),
                    operating_system: operatingSystem,
                  }
                : variantConfig?.contextValues
                  ? {
                      ...variantConfig?.contextValues,
                      operating_system: operatingSystem,
                    }
                  : {
                      ...previousConfigRef.current?.contextValues,
                      operating_system: operatingSystem,
                    },
            };
        // The backend caches context at session creation and one session is
        // made per iframe mount, so a claim switch would otherwise chat against
        // the previous claim's session. Re-create it here if the active claim
        // changed since the session was built (cashless support bot only).
        let effectiveSessionId = sessionId;
        if (botName === EBotType.CASHLESS_SUPPORT_BOT) {
          const cookieClaimId = getCookieClaimId();
          if (cookieClaimId && cookieClaimId !== sessionClaimIdRef.current) {
            const freshSessionId = await validateSession();
            if (freshSessionId) effectiveSessionId = freshSessionId;
          }
        }
        const response = await fetch(
          chatUrl,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-plum-token': token,
              'x-session-id': effectiveSessionId,
            },
            body: JSON.stringify(chatBody),
          },
        );

        if (!response.ok) {
          // Handle non-2xx responses here
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        if (!response.body) {
          throw new Error('No response body available');
        }

        if (response.status === 421) {
          setIsValidatingSession(true);
          await validateSession();
          return;
        }

        // Handle non-streaming JSON response
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          setLoadingStates((prev) => new Map(prev.set(queryId, false)));
          if (data.answer) {
            setChunkFinalMessage(data.answer);
            updateChunks(data.answer, queryId, data.conversation_id);
          }
          if (data.options && data.options.length > 0) {
            setShowOptions(true);
            setOptions(data.options);
          } else if (data.options !== undefined) {
            setShowOptions(false);
            setOptions([]);
          }
          setIsStreaming(false);
          setIsOptionStreaming(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedData = '';

        // Set initial loading state
        setLoadingStates((prev) => new Map(prev.set(queryId, false)));
        setIsStreaming(true);
        setIsOptionStreaming(true);

        let tempOptions: OptionProps[] = [];
        let isError = false;

        // V3 bot streaming handler
        if (isV3Bot) {
          let hasReceivedResponseChunk = false;

          // Initialize V3 response with thought state
          setResponses((prev) =>
            prev.map((item) =>
              item.id === queryId
                ? {
                    ...item,
                    isV3Response: true,
                    isV3Streaming: true,
                    v3ThoughtState: {
                      isActive: true,
                      isCollapsed: false,
                      hasTools: false,
                      toolCalls: [],
                    },
                  }
                : item,
            ),
          );

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            accumulatedData += decoder.decode(value, { stream: true });

            let endIndex;
            while ((endIndex = accumulatedData.indexOf('\n')) > -1 && !isError) {
              const line = accumulatedData.slice(0, endIndex).trim();
              accumulatedData = accumulatedData.slice(endIndex + 1);

              if (line) {
                try {
                  const event: IV3StreamEvent = JSON.parse(line);

                  switch (event.type) {
                    case EV3StreamEventType.TOOL_START:
                      if (event.tool_data) {
                        setResponses((prev) =>
                          prev.map((item) =>
                            item.id === queryId
                              ? {
                                  ...item,
                                  v3ThoughtState: {
                                    ...item.v3ThoughtState!,
                                    isActive: true,
                                    hasTools: true,
                                    toolCalls: [
                                      ...item.v3ThoughtState!.toolCalls,
                                      {
                                        tool_name: event.tool_data!.tool_name,
                                        display_name: event.tool_data!.display_name,
                                        status: 'in_progress' as const,
                                      },
                                    ],
                                  },
                                }
                              : item,
                          ),
                        );
                      }
                      break;

                    case EV3StreamEventType.TOOL_END:
                      if (event.tool_data) {
                        setResponses((prev) =>
                          prev.map((item) =>
                            item.id === queryId
                              ? {
                                  ...item,
                                  v3ThoughtState: {
                                    ...item.v3ThoughtState!,
                                    toolCalls: item.v3ThoughtState!.toolCalls.map((toolCall) =>
                                      toolCall.tool_name === event.tool_data!.tool_name
                                        ? { ...toolCall, status: 'completed' as const }
                                        : toolCall,
                                    ),
                                  },
                                }
                              : item,
                          ),
                        );
                      }
                      break;

                    case EV3StreamEventType.RESPONSE_CHUNK:
                      // First response chunk - collapse thought bubble
                      if (!hasReceivedResponseChunk) {
                        hasReceivedResponseChunk = true;
                        setResponses((prev) =>
                          prev.map((item) =>
                            item.id === queryId
                              ? {
                                  ...item,
                                  v3ThoughtState: {
                                    ...item.v3ThoughtState!,
                                    isActive: false,
                                    isCollapsed: true,
                                  },
                                }
                              : item,
                          ),
                        );
                      }
                      // Update answer - answer is accumulated, just replace
                      if (event.answer) {
                        setChunkFinalMessage(event.answer);
                        updateChunks(event.answer, queryId, '');
                      }
                      break;

                    case EV3StreamEventType.RESPONSE_COMPLETE:
                      // Final response - update with conversation_id and options
                      if (event.answer) {
                        setChunkFinalMessage(event.answer);
                        updateChunks(
                          event.answer,
                          queryId,
                          event.conversation_id || '',
                        );
                      }

                      // Mark streaming as complete
                      setResponses((prev) =>
                        prev.map((item) =>
                          item.id === queryId
                            ? {
                                ...item,
                                isV3Streaming: false,
                                v3ThoughtState: {
                                  ...item.v3ThoughtState!,
                                  isActive: false,
                                  isCollapsed: true,
                                },
                              }
                            : item,
                        ),
                      );

                      if (event.conversation_id) {
                        setConversationId(event.conversation_id);
                      }

                      if (event.options && event.options.length > 0) {
                        setIsOptionStreaming(false);
                        setShowOptions(true);
                        setOptions(event.options);
                        tempOptions = event.options;
                      } else if (event.options !== undefined) {
                        setIsOptionStreaming(false);
                        setShowOptions(false);
                        setOptions([]);
                        tempOptions = [];
                      }
                      break;

                    default:
                      // Handle legacy format for backward compatibility
                      if (event.answer || event.conversation_id) {
                        if (event.answer) {
                          setChunkFinalMessage(event.answer);
                        }
                        updateChunks(
                          event.answer || chunkFinalMessage,
                          queryId,
                          event.conversation_id || '',
                        );
                      }
                  }

                  // Handle escalation if present
                  if (event.escalation !== undefined) {
                    setEscalation(event.escalation);
                  }

                  // Handle source if present
                  if (event.source) {
                    setSource(event.source);
                  }

                  // Handle error if present
                  if (event.error) {
                    track({
                      event: MIXPANEL_EVENTS.BOT_ERROR,
                      data: {
                        payloadData,
                        error: event.error,
                      },
                    });
                    handleBotError({
                      botName,
                      payloadData,
                      queryIdRef,
                      setResponses,
                      setOptionClicked,
                      statusCode: event.error.status_code,
                      code: event.error.code,
                      message: event.error.message,
                    });
                    isError = true;
                  }
                } catch (e) {
                  console.warn('Failed to parse V3 streaming JSON chunk:', line, e);
                }
              }
            }
          }
        } else {
          // Non-V3 bot streaming handler (original logic)
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            accumulatedData += decoder.decode(value, { stream: true });

            let endIndex;
            while ((endIndex = accumulatedData.indexOf('\n')) > -1 && !isError) {
              const line = accumulatedData.slice(0, endIndex).trim();
              accumulatedData = accumulatedData.slice(endIndex + 1);

              if (line) {
                try {
                  const item = JSON.parse(line);

                  if (item.answer || item.conversation_id) {
                    if (item.answer) {
                      setChunkFinalMessage(item.answer);
                    }
                    updateChunks(
                      item.answer || chunkFinalMessage,
                      queryId,
                      item.conversation_id,
                    );
                  }

                  if (item.options) {
                    setIsOptionStreaming(false);
                    setShowOptions(true);
                    setOptions(item.options);
                    tempOptions = item.options;
                  } else {
                    setShowOptions(false);
                  }

                  if (item.conversation_id) {
                    setConversationId(item.conversation_id);
                  }

                  if (item.error) {
                    track({
                      event: MIXPANEL_EVENTS.BOT_ERROR,
                      data: {
                        payloadData,
                        error: item.error,
                      },
                    });

                    handleBotError({
                      botName,
                      payloadData,
                      queryIdRef,
                      setResponses,
                      setOptionClicked,
                      statusCode: item.error.status_code,
                      code: item.error.code,
                      message: item.error.message,
                    });
                  }

                  if (item.escalation !== undefined) {
                    setEscalation(item.escalation);
                  }

                  if (item.source) {
                    setSource(item.source);
                  }
                } catch (e) {
                  console.warn('Failed to parse non-V3 streaming JSON chunk:', line, e);
                }
              }
            }
          }
        }

        // Process temp options for both V3 and non-V3
        if (tempOptions.length > 0) {
          tempOptions.forEach((currentItem: OptionProps) => {
            if (currentItem?.actionType === EOptionAction.STRAPI_ARTICLE_ID) {
              const newQueryId = queryIdRef.current++;
              setResponses((prev) => [
                ...prev,
                {
                  id: newQueryId,
                  query: '',
                  response: currentItem.title,
                  feedbackGiven: false,
                  cid: '',
                  date: new Date(),
                  actionType: EOptionAction.STRAPI_ARTICLE_ID,
                  content: currentItem.content,
                  articleType: currentItem.articleType,
                },
              ]);
            }
            if (currentItem.actionType === EOptionAction.BOT_MESSAGE) {
              const newQueryId = queryIdRef.current++;
              setResponses((prev) => [
                ...prev,
                {
                  id: newQueryId,
                  query: '',
                  response: currentItem.title,
                  feedbackGiven: false,
                  cid: '',
                  date: new Date(),
                  actionType: EOptionAction.BOT_MESSAGE,
                },
              ]);
            }
            if (currentItem.actionType === EOptionAction.PHONE) {
              const newQueryId = queryIdRef.current++;
              setResponses((prev) => [
                ...prev,
                {
                  id: newQueryId,
                  query: '',
                  response: currentItem.title,
                  feedbackGiven: false,
                  cid: '',
                  date: new Date(),
                  actionType: EOptionAction.PHONE,
                  content: currentItem.content,
                },
              ]);
            }
          });
        }

        setIsStreaming(false);
        setIsOptionStreaming(false);
        return chunkFinalMessage;
      } catch (error) {
        track({
          event: MIXPANEL_EVENTS.BOT_ERROR,
          data: {
            payloadData,
            error: error,
          },
        });
        setLoadingStates((prev) => new Map(prev.set(queryId, false)));
        setIsStreaming(false);
        setIsOptionStreaming(false);

        handleBotError({
          botName,
          payloadData,
          queryIdRef,
          setResponses,
          setOptionClicked,
        });
      }
    },
    [variantConfig, botName, user, escalation, setResponses],
  );

  useEffect(() => {
    validateSession();
  }, [user?.organisationId, user?.memberId, botName, token]);

  useEffect(() => {
    if (!isSessionValid) return; // Only proceed if session is valid

    // Outside the cookie branch so CB_INITIAL_PROMPT is handled on both paths.
    const messageListener = async (event: MessageEvent) => {
      const { data, origin } = event;
      const payload = safeJSONParse(data);
      if (ALLOWED_PARENT_ORIGINS.includes(origin)) {
        if (payload.type === EMessageType.CB_CLAIM_DETAILS) {
          if (botName === EBotType.CASHLESS_SUPPORT_BOT) {
            // Widget hands the claim over via postMessage; build the scoped
            // cashless_claim context so the backend doesn't prefetch every skill.
            const cashlessContext = buildCashlessClaimContext(
              payload.data,
              operatingSystem,
            ) as unknown as Record<string, string>;
            const updatedConfig = {
              // Ref is null on the first message; fall back so getInitialMessage survives.
              ...(previousConfigRef.current ?? VariantConfigs.get(botName) ?? {}),
              variant: botName ?? '',
              contextValues: cashlessContext,
            } as IBotVariantConfig;
            setPayloadData(cashlessContext);
            setVariantConfig(updatedConfig);
            previousConfigRef.current = updatedConfig;
          } else {
            setPayloadData(payload.data);
            if (!payloadData) {
              setVariantConfig((prev) => ({
                ...prev,
                variant: botName ?? '',
                contextValues: {
                  ...prev?.contextValues,
                  payloadData: payload.data,
                },
              }));
            }

            setPayloadData(payload.data);
          }
        }
        if (payload.type === EMessageType.CB_INITIAL_PROMPT) {
          const queryFromPrompt: string = payload.data;
          setQuery(data);
          if (queryIdRef.current === 0) {
            setIsInitialText(false);
          }
          const queryId = queryIdRef.current++;
          setResponses((prev) => [
            ...prev,
            {
              id: queryId,
              query: queryFromPrompt,
              response: '',
              feedbackGiven: false,
              cid: '',
              date: new Date(),
            },
          ]);
          setQuery('');
          setIsAnswerLoading(queryId);

          await getAnswer(queryFromPrompt, queryId);
        }
      }
    };
    window.addEventListener('message', messageListener);

    // Advertise readiness only after the listener is attached, else the parent
    // (which posts on READY) can fire CB_INITIAL_PROMPT into the gap before this
    // session-gated effect runs - the drop seen on mobile, where the iframe
    // loads at submit time so attach and advertise race in one burst.
    if (window.parent) {
      postMessageToParent(EMessageType.READY, [EMessageType.CB_INITIAL_PROMPT]);
    }

    let botContext = Cookies.get(AppConfig.BOT_CONTEXT) || '';
    if (botContext && botName && botWithContext.has(botName as EBotType)) {
      botContext = JSON.parse(botContext);
      const config = VariantConfigs.get(botName);
      if (config) {
        const updatedConfig = {
          ...config,
          contextValues: botContext as unknown as Record<string, string>,
        };
        if (
          JSON.stringify(previousConfigRef.current) !==
          JSON.stringify(updatedConfig)
        ) {
          setVariantConfig({ ...updatedConfig });
          previousConfigRef.current = updatedConfig; // Update the ref to the new config
        }
        setPayloadData((prevPayloadData: unknown) => {
          if (JSON.stringify(prevPayloadData) !== JSON.stringify(botContext)) {
            return botContext;
          }
          return prevPayloadData; // Return the previous state if no change
        });
      }
      const botInitialMessage = Cookies.get(AppConfig.BOT_INITIAL_QUESTION);
      if (botInitialMessage) {
        Cookies.remove(AppConfig.BOT_INITIAL_QUESTION, COOKIE_CONFIG);
        const queryId = queryIdRef.current++;
        if (queryIdRef.current === 1) {
          setIsInitialText(false);
          setResponses((prev) => [
            ...prev,
            {
              id: queryId,
              query: botInitialMessage,
              response: '',
              feedbackGiven: false,
              cid: '',
              date: new Date(),
            },
          ]);
          setQuery('');
          setIsAnswerLoading(queryId);
          getAnswer(botInitialMessage, queryId).then(() =>
            setIsAnswerLoading(queryId),
          );
        }
      }
    } else {
      if (window.parent) {
        postMessageToParent(EMessageType.READY, [
          EMessageType.CB_CLAIM_DETAILS,
        ]);
      }
    }

    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [isSessionValid, getAnswer, setVariantConfig, isInitialText]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!query.trim()) return;

    const queryId = queryIdRef.current++;
    setResponses((prev) => [
      ...prev,
      {
        id: queryId,
        query,
        response: '',
        feedbackGiven: false,
        cid: '',
        date: new Date(),
      },
    ]);
    setQuery('');
    setIsAnswerLoading(queryId);

    // Call the API to get the response
    handleScrollToBottomClick();
    await getAnswer(query, queryId);
  };


  const updateChunks = (answer: string, queryId: number, cid: string) => {
    setResponses((prevResponses) =>
      prevResponses.map((item) =>
        item.id === queryId
          ? { ...item, response: answer, cid, date: new Date() }
          : item,
      ),
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuery(event.target.value);
  };

  const date = new Date();

  const handleBackClick = () => {
    const params = new URLSearchParams(location.search);
    if (params.get('parent') === 'plum-app') {
      window.location.href = `${AppConfig.SUPPORT_CENTER_BASE_URL}`; // Redirect to support center if parent field is present in URL
    } else if (params.get('parent') === 'support-center') {
      window.history.back();
    } else if (window.parent && usesIframeCloseBehaviour(botName)) {
      const duration = pauseTimer();
      track({
        event: MIXPANEL_EVENTS.BOT_CLOSED,
        data: {
          payloadData,
          sessionLength: duration,
          userMessageCount: responses.length,
          escalation: escalation,
          bot: botName,
        },
      });
      postMessageToParent(EMessageType.CLOSE_IFRAME, true);
    } else if (isV3Bot) {
      // For V3 bots with direct access (no parent), use native close to go back to caller
      window?.NativeBridge?.onNativeClose?.();
    } else if (botName === EBotType.HEALTH_CHECKUP_BOT) {
      window?.NativeBridge?.onNativeClose?.();
    } else {
      // Default: redirect to support center for direct bot access
      window.location.href = `${AppConfig.SUPPORT_CENTER_BASE_URL}`;
    }
  };

  const shouldRenderPlumLogoAndDate = (index: number) => {
    const nextMessage = responses[index + 1];
    return !nextMessage || nextMessage.query?.length > 0;
  };

  const showOptionButtons =
    !optionClicked &&
    responses[responses.length - 1]?.id === queryIdRef.current - 1 &&
    !isOptionStreaming &&
    (options.some((option) => option.actionType === EOptionAction.BOT_BUTTON) ||
      options.some((option) => option.actionType === EOptionAction.DEEP_LINK) ||
      options.some((option) => option.actionType === EOptionAction.TICKET));

  const renderResponseContent = (item: any) => {
    const InterceptedLinkWrapper = (props: any) => {
      return <InterceptedLink {...props} isWebView={isWebView} />;
    };
    switch (item.actionType) {
      case 'error':
        return <span>{item.response}</span>;
      case 'phone':
        return (
          <a target="_blank" href={`tel:${item.content}`}>
            📞
            <span className="text-red-500 underline">{item.response}</span>
          </a>
        );
      case 'strapiArticleId':
        return (
          <Button
            intent={'tertiary'}
            className="text-red-500 underline p-0 h-0 font-normal min-w-0"
            onClick={() =>
              handleStrapiArticleButtonClick(
                item.content || '',
                item.articleType,
              )
            }
          >
            {item.response}
          </Button>
        );
      case 'calendly':
        return (
          <button onClick={() => handleCalendlyLink(CALENDLY_URL)}>
            🗓️
            <span className="text-red-500 underline">{item.response}</span>
          </button>
        );
      case 'restart':
        return (
          <button
            onClick={() => {
              window.location.reload();
            }}
          >
            <span className="text-red-500 underline">{item.response}</span>
          </button>
        );
      default:
        return (
          <ReactMarkdown
            remarkPlugins={[gfm]}
            className="prose text-[14px]"
            urlTransform={(uri) => {
              if (uri.startsWith('tel:')) {
                return uri; // Allow tel: links
              }
              return uri || ''; // Default behavior for other links
            }}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: InterceptedLinkWrapper,
            }}
          >
            {item.response}
          </ReactMarkdown>
        );
    }
  };

  const { BotPrompt, botPromptInitialOptions, getInitialMessage } =
    variantConfig || {};

  const showBotPrompt =
    responses.length === 0 && BotPrompt && botPromptInitialOptions;

  const handleDisclaimerBottomsheetClick = () => {
    setIsDisclaimerBottomsheetOpen(false);
  };

  if (isValidatingSession) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          <div className="w-5 h-5 animate-spin rounded-full border-t-transparent border-solid border-mobile-primary-500 border-2" />
          <p className="mt-5 text-gray-400 text-sm">
            Setting up the chat environment for you
          </p>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <Layout.Main className="h-full my-auto">
        <div className="fixed text-center text-sm font-medium leading-[20px] left-1/2 top-0 transform -translate-x-1/2 w-full bg-white p-3 px-4">
          <button className="flex" type="button" onClick={handleBackClick}>
            <BackChevronMobile />
          </button>
        </div>
        <ChatErrorScreen
          title={''}
          subtitle={'Something went wrong'}
          handleCtaClick={() => {
            window.location.reload();
          }}
        />
      </Layout.Main>
    );
  }

  return (
    <Layout.Main
      rootClassName="h-[100vh] h-[100dvh]"
      className="h-full"
      footerProps={{
        isSticky: true,
        children: (
          <>
            {showBotPrompt && (
              <BotPrompt
                options={botPromptInitialOptions}
                conversationId={conversationid}
                setOptionClicked={setOptionClicked}
                handleSubmit={handleSubmit}
                setQuery={setQuery}
                getAnswer={getAnswer}
              />
            )}
            <ScrollButton
              handleScrollToBottomClick={handleScrollToBottomClick}
              isVisible={showScrollButton}
            />
            <AskMeAnything
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              isSubmitEnabled={isSubmitEnabled}
              query={query}
              isStreaming={isStreaming}
            />
          </>
        ),
      }}
    >
      {botName === EBotType.POLICYGPTV2 && (
        <BottomSheet
          className="h-min"
          mode="midway"
          dismissable={false}
          open={isDisclaimerBottomsheetOpen}
          onClose={handleDisclaimerBottomsheetClick}
        >
          <DisclaimerContent handleClick={handleDisclaimerBottomsheetClick} />
        </BottomSheet>
      )}
      <div className="header flex flex-row fixed text-center text-sm font-medium leading-[20px] left-1/2 transform -translate-x-1/2 w-full bg-white p-3 px-4">
        <button className="flex" type="button" onClick={handleBackClick}>
          <BackChevronMobile />
        </button>
        <div role="banner" className="mx-auto">
          Chat with Plum
        </div>
      </div>
      <div
        role="chat"
        className="flex flex-col w-full overflow-x-hidden overflow-y-scroll h-full"
        ref={chatContainerRef}
      >
        <div className="w-full">
          <div className="mt-[72px] flex flex-col items-center mb-[80px]">
            {isInitialText && (
              <div className="flex items-start flex-col self-start max-w-[90%] break-words text-sm m-2">
                <div>
                  <div className="flex items-end">
                    <img
                      data-testid="plum-logo"
                      src="/plumLogo.svg"
                      width={32}
                      height={32}
                    />
                    <div className="flex flex-col">
                      {botName && (
                        <p className="ml-[0.75rem] bg-mobile-gray-50 px-4 py-2 text-mobile-gray-500 rounded-tl-xl rounded-tr-xl rounded-br-xl">
                          {payloadData || variantConfig?.contextValues ? (
                            getInitialMessage?.(payloadData as TPayloadData)
                          ) : (
                            <img
                              data-testid="loading-gif"
                              src="/loading.gif"
                              width="32px"
                              height="32px"
                            />
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="ml-[42px] mt-2 text-xs text-gray-400 font-normal">
                    {date.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                    })}
                    ,{' '}
                    {date.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: true,
                    })}
                  </p>
                </div>
              </div>
            )}
            {botName === EBotType.POLICYGPTV2 &&
              !isDisclaimerBottomsheetOpen && (
                <div className="flex items-start flex-col self-start max-w-[90%] break-words text-sm m-2">
                  <div>
                    <div className="flex items-end">
                      <p className="ml-11 bg-mobile-gray-50 px-4 py-2 text-mobile-gray-500 rounded-xl">
                        Please note this is an{' '}
                        <strong>Experimental Beta</strong> and may sometimes
                        produce inaccurate information about your policy terms
                      </p>
                    </div>
                  </div>
                </div>
              )}
            {responses.length > 0 ? (
              <>
                {responses.map((item, index) => (
                  <div key={index} className="w-full flex flex-col items-start">
                    {item.query.length > 0 && (
                      <div className="flex flex-col items-end self-end content-end m-2 max-w-[81%]">
                        <p className="whitespace-pre-wrap text-sm bg-gray-500 text-white py-2 px-4 break-words text-left rounded-tl-xl rounded-tr-xl rounded-bl-xl">
                          {item.query}
                        </p>
                        <p className="ml-[42px] mt-2 text-xs text-gray-400 font-normal">
                          {item.date.toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                          })}
                          ,{' '}
                          {item.date.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true,
                          })}
                        </p>
                      </div>
                    )}
                    {/* V3 Bot Two-Bubble UI */}
                    {item.isV3Response ? (
                      <div className="w-full">
                        {/* Thought Bubble - Always shown for V3 responses with tools */}
                        {item.v3ThoughtState?.hasTools && (
                          <ThoughtBubble
                            isActive={item.v3ThoughtState?.isActive ?? false}
                            isCollapsed={item.v3ThoughtState?.isCollapsed ?? true}
                            onToggleCollapse={() => {
                              setResponses((prev) =>
                                prev.map((r) =>
                                  r.id === item.id
                                    ? {
                                        ...r,
                                        v3ThoughtState: {
                                          ...r.v3ThoughtState!,
                                          isCollapsed: !r.v3ThoughtState?.isCollapsed,
                                        },
                                      }
                                    : r,
                                ),
                              );
                            }}
                            toolCalls={item.v3ThoughtState?.toolCalls ?? []}
                          />
                        )}
                        {/* Answer Bubble - Shows when response starts streaming or thinking */}
                        {(item.response?.length > 0 || loadingStates.get(item.id) || (item.v3ThoughtState?.isActive && item.v3ThoughtState?.hasTools)) && (
                          <div className="flex items-start flex-col self-start max-w-[90%] break-words text-sm m-2">
                            <div>
                              <div className="flex items-end">
                                {shouldRenderPlumLogoAndDate(index) && (
                                  <img src="/plumLogo.svg" width={32} height={32} alt="Plum" />
                                )}
                                <div className="flex flex-col">
                                  {(loadingStates.get(item.id) && !item.v3ThoughtState?.hasTools) || (item.v3ThoughtState?.isActive && item.v3ThoughtState?.hasTools && !item.response?.length) ? (
                                    <p
                                      className="bg-mobile-gray-50 px-4 py-2 text-mobile-gray-500 rounded-tl-xl rounded-tr-xl rounded-br-xl"
                                      style={{
                                        marginLeft: shouldRenderPlumLogoAndDate(index)
                                          ? '0.75rem'
                                          : '2.8rem',
                                      }}
                                    >
                                      <img
                                        data-testid="loading-gif"
                                        src="/loading.gif"
                                        width="32px"
                                        height="32px"
                                      />
                                    </p>
                                  ) : (item.response?.length > 0 || item.isV3Streaming) ? (
                                    <p
                                      className="bg-mobile-gray-50 px-4 py-2 text-mobile-gray-500 rounded-tl-xl rounded-tr-xl rounded-br-xl"
                                      style={{
                                        marginLeft: shouldRenderPlumLogoAndDate(index)
                                          ? '0.75rem'
                                          : '2.8rem',
                                      }}
                                    >
                                      {item.isV3Streaming ? (
                                        renderResponseContent({ ...item, response: item.response || '' })
                                      ) : (
                                        renderResponseContent(item)
                                      )}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              {!isOptionStreaming &&
                                !item.isV3Streaming &&
                                shouldRenderPlumLogoAndDate(index) && (
                                  <p className="ml-[42px] mt-2 text-xs text-gray-400 font-normal">
                                    {item.date.toLocaleDateString('en-US', {
                                      month: 'long',
                                      day: 'numeric',
                                    })}
                                    ,{' '}
                                    {item.date.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: 'numeric',
                                      hour12: true,
                                    })}
                                  </p>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Non-V3 Bot Original UI */
                      <div className="flex items-start flex-col self-start max-w-[90%] break-words text-sm m-2">
                        <div>
                          <div className="flex items-end">
                            {shouldRenderPlumLogoAndDate(index) && (
                              <img src="/plumLogo.svg" width={32} height={32} alt="Plum" />
                            )}
                            <div className="flex flex-col">
                              {loadingStates.get(item.id) ? (
                                <p
                                  className="bg-mobile-gray-50 px-4 py-2 text-mobile-gray-500 rounded-tl-xl rounded-tr-xl rounded-br-xl"
                                  style={{
                                    marginLeft: shouldRenderPlumLogoAndDate(index)
                                      ? '0.75rem'
                                      : '2.8rem',
                                  }}
                                >
                                  <img
                                    data-testid="loading-gif"
                                    src="/loading.gif"
                                    width="32px"
                                    height="32px"
                                  />
                                </p>
                              ) : (
                                item.response?.length > 0 && (
                                  <p
                                    className="bg-mobile-gray-50 px-4 py-2 text-mobile-gray-500 rounded-tl-xl rounded-tr-xl rounded-br-xl"
                                    style={{
                                      marginLeft: shouldRenderPlumLogoAndDate(
                                        index,
                                      )
                                        ? '0.75rem'
                                        : '2.8rem',
                                    }}
                                  >
                                    {renderResponseContent(item)}
                                  </p>
                                )
                              )}
                            </div>
                          </div>
                          {!isOptionStreaming &&
                            shouldRenderPlumLogoAndDate(index) && (
                              <p className="ml-[42px] mt-2 text-xs text-gray-400 font-normal">
                                {item.date.toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                })}
                                ,{' '}
                                {item.date.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: 'numeric',
                                  hour12: true,
                                })}
                              </p>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {showOptionButtons && (
                  <Options
                    options={options}
                    conversationId={conversationid}
                    setOptionClicked={setOptionClicked}
                    handleSubmit={handleSubmit}
                    setQuery={setQuery}
                    getAnswer={getAnswer}
                    isDisabled={isStreaming}
                  />
                )}
                <input className="h-0 w-0" ref={screenBottom} />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Layout.Main>
  );
};

export default ChatUI;
